// components/designer/CanvasComponent.tsx
/**
 * Canvas Component
 *
 * Individual draggable/resizable component renderer on the canvas.
 * Shows selection ring, resize handles, and action buttons.
 */

import {
  Copy,
  Eye,
  EyeOff,
  Lock,
  RotateCcw,
  Trash2,
  Unlock,
} from "lucide-react";
import React from "react";
import { NormalizedComponent } from "../../types";

interface CanvasComponentProps {
  component: NormalizedComponent;
  isSelected: boolean;
  tool: "select" | "move";
  renderedHtml: string;
  onClick: (e: React.MouseEvent) => void;
  onMouseDown: (
    e: React.MouseEvent,
    isResize?: boolean,
    handle?: string
  ) => void;
  onContextMenu: (e: React.MouseEvent) => void;
  onUpdate: (updates: Partial<NormalizedComponent>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

const CanvasComponent: React.FC<CanvasComponentProps> = ({
  component,
  isSelected,
  tool,
  renderedHtml,
  onClick,
  onMouseDown,
  onContextMenu,
  onUpdate,
  onDelete,
  onDuplicate,
}) => {
  return (
    <div
      className={`absolute transition-all duration-150 ${
        isSelected
          ? "ring-2 ring-blue-500 ring-offset-1"
          : "hover:ring-2 hover:ring-blue-300 hover:ring-offset-1"
      } ${
        component.isLocked
          ? "cursor-not-allowed"
          : tool === "move"
          ? "cursor-move"
          : "cursor-pointer"
      }`}
      style={{
        left: component.x,
        top: component.y,
        width: component.width,
        height: component.height,
        zIndex: component.zIndex,
        display: component.isVisible ? "block" : "none",
        backgroundColor: component.style?.backgroundColor,
        border: `${component.style?.borderWidth}px ${component.style?.borderStyle} ${component.style?.borderColor}`,
        borderRadius: `${component.style?.borderRadius}px`,
        padding: `${component.style?.padding}px`,
        fontSize: `${component.style?.fontSize}px`,
        fontWeight: component.style?.fontWeight,
        color: component.style?.color,
        transform: `rotate(${component.rotation}deg)`,
        textAlign: component.style?.textAlign,
      }}
      onClick={onClick}
      onMouseDown={(e) => onMouseDown(e)}
      onContextMenu={onContextMenu}
    >
      <div
        className={`w-full h-full overflow-hidden ${
          component.alias === "text_box" ? "" : "select-none"
        }`}
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
        onBlur={(e) => {
          const editableEl = e.currentTarget.querySelector(
            '[data-role="editable-content"]'
          );
          if (editableEl) {
            onUpdate({ content: editableEl.innerHTML });
          }
        }}
        contentEditable={component.alias === "text_box" ? undefined : false} // Allow children to be editable
      />

      {isSelected && !component.isLocked && (
        <>
          {/* Resize Handles */}
          <div
            className="absolute w-3 h-3 bg-blue-500 rounded-full -right-1.5 -bottom-1.5 cursor-se-resize"
            onMouseDown={(e) => {
              e.stopPropagation();
              onMouseDown(e, true, "se");
            }}
          />
          <div
            className="absolute w-3 h-3 bg-blue-500 rounded-full -left-1.5 -bottom-1.5 cursor-sw-resize"
            onMouseDown={(e) => {
              e.stopPropagation();
              onMouseDown(e, true, "sw");
            }}
          />
          <div
            className="absolute w-3 h-3 bg-blue-500 rounded-full -right-1.5 -top-1.5 cursor-ne-resize"
            onMouseDown={(e) => {
              e.stopPropagation();
              onMouseDown(e, true, "ne");
            }}
          />
          <div
            className="absolute w-3 h-3 bg-blue-500 rounded-full -left-1.5 -top-1.5 cursor-nw-resize"
            onMouseDown={(e) => {
              e.stopPropagation();
              onMouseDown(e, true, "nw");
            }}
          />

          {/* Action Buttons */}
          <div className="absolute -top-8 right-0 flex items-center space-x-1 bg-white shadow-md rounded px-1 py-0.5">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate({ isVisible: !component.isVisible });
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title={component.isVisible ? "Hide" : "Show"}
            >
              {component.isVisible ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate({ isLocked: !component.isLocked });
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title={component.isLocked ? "Unlock" : "Lock"}
            >
              {component.isLocked ? <Lock size={14} /> : <Unlock size={14} />}
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDuplicate();
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Duplicate"
            >
              <Copy size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onUpdate({ rotation: (component.rotation + 90) % 360 });
              }}
              className="p-1 hover:bg-gray-100 rounded"
              title="Rotate"
            >
              <RotateCcw size={14} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="p-1 hover:bg-gray-100 rounded text-red-500"
              title="Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CanvasComponent;
